// Section for import npm components -----------------------------------------------------------------------------------
import React, { useContext, useEffect, useState } from 'react';

// MUI components
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import CheckIcon from '@mui/icons-material/Check';

// Section for import custom components --------------------------------------------------------------------------------
import CommonUtility from '@utils/Common';
import { AppConfigContext } from '@providers/AppConfigProvider';
import { useMeta } from '@providers/MansionNavigation/MetaProvider';
import { SelectedCitiesContext, useUpdateSelectedCities } from '@providers/MansionNavigation/SelectedCitiesProvider';

// Section for type definition -----------------------------------------------------------------------------------------
import filterEntries from '@components/MansionNavigation/Utils/Filters';
import generateSortQuery from '@components/MansionNavigation/Utils/Sort';
import CustomCheckboxEvent from '@/types/event/CustomCheckboxEvent';
import { CityApiData } from '@/types/components/MansionNavigation/CitiesApiData';
import CitiesListData, { CityListData } from '@/types/components/MansionNavigation/CitiesListData';

// --- groupCities取得用ユーティリティ関数 ---
/**
 * 指定したグループIDに属し、マンション件数が1件以上の都市リストを返すユーティリティ関数
 * @param citiesData 都市データ配列
 * @param groupId グループID
 * @returns 条件に合致する都市データ配列
 */
function getGroupCities(citiesData: CityListData[], groupId: number): CityListData[] {
  return citiesData.filter(
    (cityData: CityListData) => cityData.city_group_id === groupId && cityData.mansion_count > 0,
  );
}

// --- 選択状態管理用カスタムフック ---
/**
 * 都市選択チェックボックスの選択状態を管理するカスタムフック
 * 個別都市・全ての地域チェックボックスのハンドラを提供
 * @param citiesData 都市データ配列
 * @param selectedCities 選択中の都市ID配列
 * @param setSelectedCities 選択都市ID配列のsetter
 * @param grantSelected 選択状態をcitiesDataに反映する関数
 */
function useCitiesSelection(
  citiesData: CityListData[],
  selectedCities: number[],
  setSelectedCities: (ids: number[]) => void,
  grantSelected: (cities: CityListData[], ids: number[]) => void,
) {
  // 個別都市チェックボックスのハンドラ
  const handleCityChange = (event: CustomCheckboxEvent): void => {
    let updatedSelected = [...selectedCities];
    const cityId = Number(event.target.id);
    if (event.target.checked) {
      if (!updatedSelected.includes(cityId)) {
        updatedSelected.push(cityId);
      }
    } else {
      updatedSelected = updatedSelected.filter((id) => id !== cityId);
    }
    setSelectedCities(updatedSelected);
    grantSelected(citiesData, updatedSelected);
  };

  // 全ての地域チェックボックスのハンドラ
  const handleAllCitiesChange = (event: React.ChangeEvent<HTMLInputElement>, groupId: number): void => {
    const groupCities = getGroupCities(citiesData, groupId);
    let updatedSelected = [...selectedCities];
    if (event.target.checked) {
      groupCities.forEach((city) => {
        if (!updatedSelected.includes(Number(city.id_number))) {
          updatedSelected.push(Number(city.id_number));
        }
      });
    } else {
      updatedSelected = updatedSelected.filter((id) => !groupCities.some((city) => city.id_number === id));
    }
    setSelectedCities(updatedSelected);
    grantSelected(citiesData, updatedSelected);
  };

  return { handleCityChange, handleAllCitiesChange };
}

type CityGroupData = CityApiData[];

const commonUtil = CommonUtility;
const citiesJsonUrl: string = '/karte/api/v1/cities/';

// TODO: city group id が 1-23ku と 1-23ku 以外の2種類あるので、1-23ku は 0 に変換する。テーブルが正規化されたら削除
/**
 * APIから取得したcity_group_code_nameを内部のグループID（0 or 1）に変換する関数
 * @param value city_group_code_name
 * @returns グループID（0: 23区, 1: その他）
 */
function convertGroupId(value: string | null): number {
  if (value === '1-23ku') {
    return 0;
  }
  if (value == null) {
    return 0;
  }
  return 1;
}

/**
 * 都市リスト（CitiesList）
 * 都道府県ごとの都市グループ・都市一覧を表示し、チェックボックスで選択可能
 * @param props.selectedPrefecture 選択中の都道府県情報
 */
export default function CitiesList(props: { selectedPrefecture: any }): React.JSX.Element {
  const { selectedPrefecture } = props;
  const { isMobile } = useContext(AppConfigContext);
  const { meta } = useMeta(); // フィルタ・ソート情報取得

  // クエリパラメータ生成
  const filterQuery = filterEntries(meta.filters)
    ? `?${filterEntries(meta.filters)}${generateSortQuery(meta.sort, '&')}`
    : `${generateSortQuery(meta.sort, '&')}`;

  const apiQueryParameter: string = `?prefecture_code=${selectedPrefecture.pref_code_name}`;

  // 選択中都市IDのグローバル状態
  const selectedCities: number[] = useContext(SelectedCitiesContext);
  const setSelectedCities = useUpdateSelectedCities();

  // 都市グループ・都市データのローカル状態
  const stateCityGroupData: CityGroupData = [];
  const [cityGroupData, setCityGroupData] = useState(stateCityGroupData);
  const stateCitiesData: CitiesListData = [];
  const [citiesData, setCitiesData] = useState(stateCitiesData);

  /**
   * URLクエリから事前選択都市ID配列を取得
   */
  function getPreSelectedCityIds(): number[] {
    const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
    const preSelectedCityIds: string | null = urlParams.get('pre_selected_city_id');
    const returnValue: string[] = preSelectedCityIds ? preSelectedCityIds.split(',') : [];
    return commonUtil.convertTypeArrayNumber(returnValue);
  }

  /**
   * 選択状態をcitiesDataに反映（イミュータブルに）
   * @param checkCitiesData 都市データ配列
   * @param checkSelectedCityIds 選択中都市ID配列
   */
  function grantSelected(checkCitiesData: CitiesListData, checkSelectedCityIds: number[]): void {
    // 配列を直接変更せず新しい配列を生成
    const updated = checkCitiesData.map((cityData: CityListData) => ({
      ...cityData,
      selected: checkSelectedCityIds.includes(Number(cityData.id_number)),
    }));
    setCitiesData(updated);
  }

  /**
   * 初期選択都市ID配列に含まれるか判定
   */
  function grantPreSelected(citiesDataId: number, checkPreSelectedCityIds: number[]): boolean {
    return checkPreSelectedCityIds.includes(citiesDataId);
  }

  /**
   * 都市一覧API取得
   */
  const getCities = (url: string) => {
    return new Promise((resolve) => {
      fetch(url)
        .then((res) => res.json())
        .then((data) => resolve(data));
    });
  };

  /**
   * APIレスポンスから都市データ配列・グループデータ配列を生成し状態にセット
   */
  const loadCities = async (data: any, checkPreSelectedCityIds: number[]): Promise<void> => {
    const citiesRecord: any = await Promise.all(
      data.results.map((city: any) => {
        const citiesRecordMap: CityListData = {
          key: `cities-${commonUtil.convertNumberToString(city.id)}`,
          id: commonUtil.convertNumberToString(city.id),
          id_number: city.id,
          city_group_id: convertGroupId(city.city_group_code_name),
          // TODO:テーブルが正規化されたら削除
          // city_group_id: city.city_group_id,
          city_group_id_sort: city.city_group_id_sort,
          city_group_name: city.city_group_name,
          city_group_code_name: city.city_group_code_name,
          city_groups_primary_sort_id: city.city_groups_primary_sort_id,
          city_groups_secondary_sort_id: city.city_groups_secondary_sort_id,
          jis_code_city_id: city.jis_code_city_id,
          mrweb_city_id: city.mrweb_city_id,
          prefecture_id: city.prefecture_id,
          city_code_name: city.city_code_name,
          city_type: city.city_type,
          city_name: city.city_name,
          district_name: city.district_name,
          city_kana: city.city_kana,
          primary_sort_id: city.primary_sort_id,
          secondary_sort_id: city.secondary_sort_id,
          mansion_count: city.mansion_count,
          estate_mansion_count: city.estate_mansion_count,
          selected: grantPreSelected(city.id, checkPreSelectedCityIds),
          created_at: city.created_at,
          updated_at: city.updated_at,
        };
        return citiesRecordMap;
      }),
    );
    const initCityGroupData: any = Object.values(commonUtil.groupBy(citiesRecord, 'city_group_code_name'));
    setCityGroupData(initCityGroupData);
    setCitiesData(citiesRecord);
  };

  /**
   * 初回マウント時に都市データを取得し、事前選択都市IDを反映
   */
  useEffect(() => {
    const preSelectedCityIds = getPreSelectedCityIds();
    setSelectedCities(preSelectedCityIds);
    const fetchCitiesData = async (): Promise<void> => {
      const data: any = await getCities(citiesJsonUrl + apiQueryParameter);
      loadCities(data, preSelectedCityIds);
    };
    fetchCitiesData();
  }, []);

  // --- カスタムフックでロジックを分離 ---
  const { handleCityChange, handleAllCitiesChange } = useCitiesSelection(
    citiesData,
    selectedCities,
    setSelectedCities,
    grantSelected,
  );

  // Section for render component --------------------------------------------------------------------------------------
  return (
    <Box className="md:mx-24">
      {isMobile ? (
        // Section for render Mobile mode ------------------------------------------------------------------------------
        <Container className="p-0">
          <List key="citiesList" className="p-0">
            {cityGroupData.map((cityGroup: any, index: number) => (
              <ListItem key={`prefecture-group-${index.toString()}`} className="block p-0">
                <List
                  key={cityGroup.key}
                  className={`cities_list p-0 ${
                    index === cityGroupData.length - 1 ? 'mb-0' : 'mb-8'
                  } border-b-[#D9D9D9] border-b border-solid`}
                >
                  <Box className="flex justify-between pb-4 border-b-[#D5D5D5] border-b border-solid">
                    <Typography
                      variant="h2"
                      gutterBottom
                      className="text-lg font-bold tracking-normal mb-0 mx-5 pl-[18px] border-l-2 border-l-[#191919] border-solid"
                    >
                      {selectedPrefecture.pref_name}
                      {cityGroupData.length > 1 && ' - '}
                      {cityGroup[index].city_group_name}
                    </Typography>
                    {/* グループ内全都市一括選択チェックボックス */}
                    <Box className="flex items-center mr-5">
                      {(() => {
                        const groupCities = getGroupCities(citiesData, cityGroup[index].city_group_id);
                        return (
                          <Checkbox
                            className="cities_check"
                            id={`all_cities_${cityGroup[index].city_group_id}`}
                            checked={
                              groupCities.length > 0 &&
                              groupCities.every((city) => selectedCities.includes(Number(city.id_number)))
                            }
                            indeterminate={
                              groupCities.some((city) => selectedCities.includes(Number(city.id_number))) &&
                              !groupCities.every((city) => selectedCities.includes(Number(city.id_number)))
                            }
                            onChange={(e) => handleAllCitiesChange(e, cityGroup[index].city_group_id)}
                          />
                        );
                      })()}
                      <CheckIcon className="cities_check_icon hidden absolute w-5 h-5 text-white" />
                      <Typography className="text-sm font-bold leading-5 tracking-normal text-left text-[#191919]">
                        全ての地域
                      </Typography>
                    </Box>
                  </Box>
                  {/* 都市リスト（モバイル） */}
                  {citiesData
                    .filter((cityData: any) => cityData.city_group_id === cityGroup[index].city_group_id)
                    .map((city: any) => (
                      <ListItem
                        key={`prefecture-${cityGroup[index].city_group_id}-${city.id}`}
                        className="cities relative w-[calc(100%_-_20px)] ml-5 border-b-[#D9D9D9] border-b border-solid p-0"
                      >
                        {city.mansion_count > 0 ? (
                          <>
                            <Checkbox
                              className="cities_check mr-3 p-0"
                              onChange={handleCityChange}
                              id={city.id}
                              checked={city.selected}
                            />
                            <CheckIcon className="cities_check_icon hidden absolute w-5 h-5 text-white" />
                            <Link
                              href={`/karte/${selectedPrefecture.pref_code_name}/cities/${city.city_code_name}/${filterQuery}`}
                              className="block w-full py-[18px] text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919] decoration-[#191919] underline-offset-[0.2rem] no-underline"
                            >
                              {city.city_name}
                            </Link>
                          </>
                        ) : (
                          <>
                            <Checkbox className="cities_check mr-3 p-0" id={city.id} disabled />
                            <Typography className="opacity-60 py-[18px] text-[15px] font-medium leading-5 tracking-normal text-left">
                              {city.city_name}
                            </Typography>
                          </>
                        )}
                      </ListItem>
                    ))}
                </List>
              </ListItem>
            ))}
          </List>
        </Container>
      ) : (
        // Section for render PC mode ------------------------------------------------------------------------------
        <Box>
          <List key="citiesList" className="p-0">
            {cityGroupData.map((cityGroup: any, index: number) => {
              const groupId = cityGroup[index].city_group_id;
              return (
                <ListItem key={`prefecture-group-${index.toString()}`} className="block w-full p-0 mt-6">
                  <List key={cityGroup.key} className="flex flex-wrap justify-start p-0">
                    <Box className="md:flex md:justify-between w-full mb-6 pb-3 border-b-[#D5D5D5] border-b border-solid">
                      <Typography
                        variant="h2"
                        gutterBottom
                        className="text-lg font-bold leading-6 tracking-[0.08em] text-left pl-4 border-l-2 border-[#191919] mb-0"
                      >
                        {selectedPrefecture.pref_name}
                        {cityGroupData.length > 1 && ' - '}
                        {cityGroup[index].city_group_name}
                      </Typography>
                      {/* グループ内全都市一括選択チェックボックス（PC） */}
                      <Box className="flex items-center gap-2">
                        <Checkbox
                          id={`all_cities_${groupId}`}
                          className="cities_check"
                          checked={
                            getGroupCities(citiesData, groupId).length > 0 &&
                            getGroupCities(citiesData, groupId).every((city) =>
                              selectedCities.includes(Number(city.id_number)),
                            )
                          }
                          indeterminate={
                            getGroupCities(citiesData, groupId).some((city) =>
                              selectedCities.includes(Number(city.id_number)),
                            ) &&
                            !getGroupCities(citiesData, groupId).every((city) =>
                              selectedCities.includes(Number(city.id_number)),
                            )
                          }
                          onChange={(e) => handleAllCitiesChange(e, groupId)}
                        />
                        <CheckIcon className="cities_check_icon hidden absolute w-5 h-5 text-white" />
                        <Typography className="text-sm font-bold leading-5 tracking-normal text-left text-[#191919]">
                          全ての地域
                        </Typography>
                      </Box>
                    </Box>
                    {/* 都市リスト（PC） */}
                    {citiesData
                      .filter((cityData: CityListData) => cityData.city_group_id === groupId)
                      .map((city: CityListData) => (
                        <ListItem
                          key={`prefecture-${groupId}-${city.id}`}
                          className="cities relative flex p-0 mb-6 w-1/4 self-center"
                        >
                          {city.mansion_count > 0 ? (
                            <>
                              <Checkbox
                                className="cities_check"
                                onChange={handleCityChange}
                                id={city.id}
                                checked={city.selected}
                              />
                              <CheckIcon className="cities_check_icon hidden absolute w-5 h-5 text-white" />
                              <Link
                                href={`/karte/${selectedPrefecture.pref_code_name}/cities/${city.city_code_name}/${filterQuery}`}
                                className="text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919] decoration-[#191919] underline-offset-[0.2rem] rf-hover-text"
                              >
                                {city.city_name}
                              </Link>
                            </>
                          ) : (
                            <>
                              <Checkbox className="cities_check" id={city.id} disabled />
                              <Typography className="opacity-60 text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919]">
                                {city.city_name}{' '}
                              </Typography>
                            </>
                          )}
                        </ListItem>
                      ))}
                  </List>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );
}
